import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, ChevronUp, CheckCircle, Circle,
  MessageSquare, FileText, Target, Zap,
  Save, AlertCircle, Trash2, Plus, RefreshCw,
  ArrowRight, ArrowLeft, Info, ExternalLink,
  ChevronsUpDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@bizbuilder/ui-core';
import { Button } from '@bizbuilder/ui-core';
import { Input } from '@bizbuilder/ui-core';
import { Badge } from '@bizbuilder/ui-core';
import { Textarea } from '@bizbuilder/ui-core';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@bizbuilder/ui-core';
import { Checkbox } from '@bizbuilder/ui-core';
import CommunicationHistory from '../../../views/Communication/CommunicationHistory';
import { cjmsApi, CJMSClient, CJMS_STAGES, CommunicationEntry } from '../api/cjmsApi';
import { getStageContent } from '../api/stageContent';
import { IntelligencePanel } from './IntelligencePanel';
import { useCjmsStore } from '../store/useCjmsStore';
import { feedbackBus } from '../services/feedbackBus';

// Phase 3: CJMS Framework UI Gaps — Lead Conversion
import { BANTForm } from './BANTForm';
import { DiscoveryCallScheduler } from './DiscoveryCallScheduler';
import { DemoChecklist } from './DemoChecklist';
import { TouchTracker } from './TouchTracker';

// Phase 3: CJMS Framework UI Gaps — Fulfillment
import { ProvisioningStatus } from './tabs/ProvisioningStatus';
import { ActivationTasks } from './tabs/ActivationTasks';
import { LessonUnlock } from './tabs/LessonUnlock';

// Phase 3: CJMS Framework UI Gaps — Retention
import { HealthCheckPanel } from './tabs/HealthCheckPanel';
import { MonthlyReview } from './tabs/MonthlyReview';
import { GoalUpsell } from './tabs/GoalUpsell';

// Phase 3: CJMS Framework UI Gaps — Growth
import { UpsellTracker } from './tabs/UpsellTracker';
import { SOPDocumenter } from './tabs/SOPDocumenter';
import { SegmentationTool } from './tabs/SegmentationTool';

// Phase 3: CJMS Framework UI Gaps — Advocacy
import { ReviewRequester } from './tabs/ReviewRequester';
import { AdvisoryBoard } from './tabs/AdvisoryBoard';
import { CoCreationWizard } from './tabs/CoCreationWizard';
import { ReferralEnrolment } from './tabs/ReferralEnrolment';

interface ClientDetailViewProps {
  client: CJMSClient;
  onBack?: () => void;
  onStageChange?: (nextStage: string) => void;
  onUpdate?: () => void;
  onCommunicationLoaded?: (email: string, fields: {
    lastCommunication?: string;
    lastCommunicationContent?: string;
    lastCommunicationChannel?: string;
  }) => void;
}

export const ClientDetailView: React.FC<ClientDetailViewProps> = ({ client, onBack, onStageChange, onUpdate, onCommunicationLoaded }) => {
  if (!client) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <p className="text-slate-500">No client selected.</p>
      </div>
    );
  }

  const storeActions = useCjmsStore(state => state.actions);

  const [activeTab, setActiveTab] = useState('journey');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    steps: true,
    resources: false,
    notes: false,
    internal: false,
    stagePicker: false,
  });

  const [notes, setNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [internalActivities, setInternalActivities] = useState<any>(null);
  const [isSavingActivities, setIsSavingActivities] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [communicationHistory, setCommunicationHistory] = useState<CommunicationEntry[]>([]);
  const [isLoadingComms, setIsLoadingComms] = useState(false);
  const [optimisticTasks, setOptimisticTasks] = useState<Map<string, boolean>>(new Map());
  const [isSyncingComms, setIsSyncingComms] = useState(false);


  const isLead = client.source === 'lead';
  const isLeadGen = client.currentStage === 'Lead Generation';
  const stageContent = useMemo(() => getStageContent(client.currentStage, isLead), [client.currentStage, isLead]);

  const completedTasks = useMemo(() => {
    try {
      const latestStatus = new Map<string, {completed: boolean, timestamp: string}>();
      if (Array.isArray(communicationHistory)) {
        // Process history from oldest to newest to keep the latest status
        [...communicationHistory].reverse().forEach(entry => {
          if (entry && entry.taskData && entry.taskData.stage === client.currentStage) {
            latestStatus.set(entry.taskData.taskKey, {
                completed: entry.taskData.completed,
                timestamp: entry.taskData.timestamp || ''
            });
          }
        });
      }

      // Merge optimistic overrides for instant UI feedback
      optimisticTasks.forEach((completed, key) => {
        latestStatus.set(key, {
            completed,
            timestamp: 'Just now'
        });
      });

      return latestStatus;
    } catch (e) {
      console.error('Error calculating completedTasks:', e);
      return new Map<string, {completed: boolean, timestamp: string}>();
    }
  }, [communicationHistory, client.currentStage, optimisticTasks]);

  // Surface the latest communication to the parent so the "Last Communication"
  // metric card and the Intelligence Hub reflect the Communications tab data.
  const applyLatestCommunication = (comms: CommunicationEntry[]) => {
    if (comms.length === 0 || !onCommunicationLoaded) {return;}
    const latest = comms[0];
    if (!latest.content) {return;}
    onCommunicationLoaded(client.email, {
      lastCommunication: latest.date,
      lastCommunicationContent: latest.content,
      lastCommunicationChannel: latest.channel,
    });
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        console.log('[ClientDetailView] Loading initial data for:', client?.email);
        const [fetchedNotes, activities, comms] = await Promise.all([
          cjmsApi.getNotes(client?.email || '', client?.phone || ''),
          cjmsApi.getInternalActivities(client?.email || ''),
          storeActions.loadCommunicationHistory(
            client?.email || '',
            client?.phone || '',
            client?.id?.replace(/^(customer_|lead_)/, '')
          )
        ]);
        console.log('[ClientDetailView] Initial data loaded. Comms count:', Array.isArray(comms) ? comms.length : 'not array');
        setNotes(fetchedNotes || '');
        setInternalActivities(activities || null);
        setCommunicationHistory(Array.isArray(comms) ? comms : []);
        applyLatestCommunication(Array.isArray(comms) ? comms : []);
      } catch (err) {
        console.error('[ClientDetailView] Failed to load initial client data:', err);
      }
    };
    if (client?.email) {
        loadInitialData();
    }
  }, [client?.email, client?.phone]);

  const refreshCommunicationHistory = async () => {
    setIsLoadingComms(true);
    try {
      console.log('[ClientDetailView] Refreshing communication history for:', client.email);
      storeActions.invalidateCommCache(client.email);
      const comms = await storeActions.loadCommunicationHistory(
        client.email,
        client.phone,
        client.id.replace(/^(customer_|lead_)/, '')
      );
      console.log('[ClientDetailView] Refreshed comms. Count:', Array.isArray(comms) ? comms.length : 'not array');
      setCommunicationHistory(comms);
      applyLatestCommunication(comms);
    } catch (err) {
      console.error('[ClientDetailView] Failed to refresh communication history:', err);
    } finally {
      setIsLoadingComms(false);
    }
  };

  const handleTaskCompletion = async (params: any) => {
    const taskKey = params.taskType === 'activity'
      ? `step-${params.stepIndex}-act-${params.activityIndex}`
      : `step-${params.stepIndex}`;

    // Optimistic update: instantly toggle the checkbox in the UI
    setOptimisticTasks(prev => new Map(prev).set(taskKey, params.completed));

    try {
      await cjmsApi.setTaskCompletion(params);

      // Directly fetch updated history instead of relying on the async state setter
      const comms = await cjmsApi.fetchCommunicationHistory(
        client.email,
        client.phone,
        client.id.replace(/^(customer_|lead_)/, '')
      );
      setCommunicationHistory(comms);

      onUpdate?.();
    } catch (err) {
      console.error('Failed to update task completion:', err);
      // Rollback optimistic update on error
      setOptimisticTasks(prev => {
        const next = new Map(prev);
        next.delete(taskKey);
        return next;
      });
    } finally {
      // We only clear the optimistic override AFTER we've updated the local state with fresh data
      // To avoid the "flip-back" effect, we add a small delay or ensure the state set has propagated.
      setTimeout(() => {
        setOptimisticTasks(prev => {
          const next = new Map(prev);
          next.delete(taskKey);
          return next;
        });
      }, 100);
    }
  };

  const handleManualSync = async () => {
    setIsSyncingComms(true);
    try {
      const result = await cjmsApi.syncCommunications(client.email, client.phone);
      if (result.error) {
        alert(`Sync failed: ${result.error}`);
      } else {
        alert(`Sync complete! ${result.syncedCount} new messages synchronized.`);
        await refreshCommunicationHistory();
      }
    } catch (err: any) {
      alert(`Unexpected error during sync: ${err.message}`);
    } finally {
      setIsSyncingComms(false);
    }
  };

  const toggleSection = (id: string) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSaveNotes = async () => {
    setIsSavingNotes(true);
    try {
      const res = await cjmsApi.saveNotes({ email: client.email, phone: client.phone, notes });
      if (res.success) {
        setSaveStatus({ type: 'success', msg: 'Notes saved successfully' });
        onUpdate?.();
      } else {
        throw new Error(res.error);
      }
    } catch (err: any) {
      setSaveStatus({ type: 'error', msg: err.message || 'Failed to save notes' });
    } finally {
      setIsSavingNotes(false);
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleSaveActivities = async (updated: any) => {
    setIsSavingActivities(true);
    try {
      const res = await cjmsApi.saveInternalActivities(client.email, updated);
      if (res.success) {
        setInternalActivities(prev => ({ ...prev, ...updated }));
        setSaveStatus({ type: 'success', msg: 'Activities updated' });
        onUpdate?.();
      } else {
        throw new Error(res.error);
      }
    } catch (err: any) {
      setSaveStatus({ type: 'error', msg: err.message || 'Failed to save activities' });
    } finally {
      setIsSavingActivities(false);
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  return (
    <div className="flex flex-col h-full space-y-6 p-4 lg:p-6 bg-slate-50/50">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => onBack?.()} className="p-2">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900 truncate">{client.name}</h1>
            <div className="flex items-center gap-2 text-sm text-slate-500 truncate">
              <span className="truncate">{client.email}</span>
              {client.phone && <span className="hidden sm:inline">• {client.phone}</span>}
              <Badge variant="outline" className="ml-2 shrink-0">
                {client.source === 'lead' ? 'Lead' : 'Customer'}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`px-3 py-1 ${client.riskLevel === 'low' ? 'bg-green-100 text-green-700' : client.riskLevel === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
            Risk: {client.riskLevel?.toUpperCase()}
          </Badge>
          <Badge className="bg-blue-100 text-blue-700 border-blue-200">
            Health: {client.healthScore}%
          </Badge>
        </div>
      </div>

      {/* Key Metrics Bar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard label="Last Communication" value={client.lastCommunication || 'N/A'} icon={<MessageSquare className="w-5 h-5" />} color="blue" />
        <MetricCard label="Next Action" value={client.nextAction || 'Pending'} icon={<Target className="w-5 h-5" />} color="green" />
        <MetricCard label="Current Stage" value={client.currentStage} icon={<Zap className="w-5 h-5" />} color="purple" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-200/50 p-1">
          <TabsTrigger value="journey" className="px-6">Journey</TabsTrigger>
          <TabsTrigger value="communications" className="px-6">
            Communications
            {communicationHistory.length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[9px] font-bold rounded-full">
                {communicationHistory.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="intelligence" className="px-6">
            Intelligence
            <span className="ml-1.5 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-[9px] font-bold rounded-full">✦</span>
          </TabsTrigger>
          <TabsTrigger value="details" className="px-6">Details</TabsTrigger>
          <TabsTrigger value="finance" className="px-6">Financials</TabsTrigger>
        </TabsList>

        <TabsContent value="journey" className="mt-6 space-y-6">
          {/* Stage Stepper */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                Journey Progression
              </h3>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs gap-1.5 h-7 bg-green-50 text-green-700 border-green-200 hover:bg-green-100 hover:text-green-800"
                  onClick={handleManualSync}
                  disabled={isSyncingComms}
                >
                  <RefreshCw className={`w-3 h-3 ${isSyncingComms ? 'animate-spin' : ''}`} />
                  Sync GoWA
                </Button>
                <div className="text-xs text-slate-500">
                  Stage {CJMS_STAGES.indexOf(client.currentStage) + 1} of {CJMS_STAGES.length}
                </div>
                {/* Stage Assignment Picker */}
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs gap-1.5 h-7"
                    onClick={() => toggleSection('stagePicker')}
                  >
                    <Zap className="w-3 h-3" />
                    Assign Stage
                    <ChevronsUpDown className="w-3 h-3" />
                  </Button>
                  {expandedSections.stagePicker && (
                    <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[200px]">
                      {CJMS_STAGES.filter(s => s !== client.currentStage).map(stage => (
                        <button
                          key={stage}
                          onClick={() => {
                            onStageChange?.(stage);
                            toggleSection('stagePicker');
                          }}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-indigo-50 transition-colors flex items-center gap-2 ${
                            CJMS_STAGES.indexOf(stage) <= CJMS_STAGES.indexOf(client.currentStage) ? 'text-slate-700' : 'text-indigo-600 font-semibold'
                          }`}
                        >
                          {CJMS_STAGES.indexOf(stage) <= CJMS_STAGES.indexOf(client.currentStage) ? (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          ) : (
                            <Circle className="w-3 h-3 text-slate-300" />
                          )}
                          {stage}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="relative flex items-center justify-between overflow-x-auto pb-4 gap-2">
              {CJMS_STAGES.map((stage, idx) => {
                const isCurrent = stage === client.currentStage;
                const isPassed = CJMS_STAGES.indexOf(client.currentStage) > idx;
                return (
                  <div key={stage} className="flex flex-col items-center min-w-[120px] relative">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                      isPassed ? 'bg-green-500 text-white' :
                      isCurrent ? 'bg-blue-600 text-white ring-4 ring-blue-100' : 'bg-slate-200 text-slate-500'
                    }`}>
                      {isPassed ? <CheckCircle className="w-5 h-5" /> : <span className="text-xs">{idx + 1}</span>}
                    </div>
                    <span className={`text-[10px] font-medium mt-2 text-center uppercase tracking-tight ${
                      isCurrent ? 'text-blue-600 font-bold' : 'text-slate-500'
                    }`}>
                      {stage}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-4">
            <CollapsibleSection
              title="Steps & Activities"
              isOpen={expandedSections.steps}
              onToggle={() => toggleSection('steps')}
              icon={<CheckCircle className="w-5 h-5" />}
            >
              <div className="space-y-6">
                {stageContent.steps.map((step, sIdx) => {
                  const activityIndices = stageContent.stepActivityMap?.[sIdx] || [];
                  return (
                    <div key={sIdx} className="border-l-2 border-slate-200 pl-4 ml-2 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {completedTasks.get(`step-${sIdx}`)?.completed ? (
                            <CheckCircle className="w-6 h-6 text-green-500" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
                              {sIdx + 1}
                            </div>
                          )}
                          <h4 className={`font-semibold flex items-center gap-3 ${completedTasks.get(`step-${sIdx}`)?.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                            {step}
                            {completedTasks.get(`step-${sIdx}`)?.completed && (
                                <span className="text-[10px] text-slate-400 font-normal italic ml-1">
                                    {completedTasks.get(`step-${sIdx}`)?.timestamp}
                                </span>
                            )}
                            <Checkbox
                              checked={completedTasks.get(`step-${sIdx}`)?.completed}
                              onChange={(e) => handleTaskCompletion({
                                email: client.email,
                                stage: client.currentStage,
                                stepIndex: sIdx,
                                completed: e.target.checked,
                                taskType: 'step'
                              })}
                              className="h-4 w-4"
                            />
                          </h4>
                        </div>
                      </div>
                      {activityIndices.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
                          {activityIndices.map((actIdx) => {
                            const activity = stageContent.activities[actIdx];
                            return (
                              <div key={actIdx} className={`flex items-center gap-2 p-2 rounded-lg border transition-colors text-sm group ${
                                completedTasks.get(`step-${sIdx}-act-${actIdx}`)?.completed
                                  ? 'bg-green-50/50 border-green-100 text-slate-400'
                                  : 'bg-slate-50 border-slate-100 text-slate-600 hover:border-blue-200'
                              }`}>
                                {completedTasks.get(`step-${sIdx}-act-${actIdx}`)?.completed ? (
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                ) : (
                                  <Circle className="w-4 h-4 text-slate-300 group-hover:text-blue-400" />
                                )}
                                <div className="flex-1 flex items-start justify-between gap-2">
                                    <span className={`text-xs leading-tight ${completedTasks.get(`step-${sIdx}-act-${actIdx}`)?.completed ? 'line-through text-slate-400' : 'text-slate-600'}`}>
                                        {activity}
                                    </span>
                                    {completedTasks.get(`step-${sIdx}-act-${actIdx}`)?.completed && (
                                        <span className="text-[9px] text-slate-400 font-normal italic shrink-0 mt-0.5">
                                            {completedTasks.get(`step-${sIdx}-act-${actIdx}`)?.timestamp}
                                        </span>
                                    )}
                                </div>
                                <Checkbox
                                  checked={completedTasks.get(`step-${sIdx}-act-${actIdx}`)?.completed}
                                  onChange={(e) => handleTaskCompletion({
                                    email: client.email,
                                    stage: client.currentStage,
                                    stepIndex: sIdx,
                                    activityIndex: actIdx,
                                    completed: e.target.checked,
                                    taskType: 'activity'
                                  })}
                                  className="h-4 w-4 shrink-0"
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Stage Resources"
              isOpen={expandedSections.resources}
              onToggle={() => toggleSection('resources')}
              icon={<FileText className="w-5 h-5" />}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {stageContent.resources.map((res: any, idx: number) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100 text-sm text-slate-700 group hover:bg-white hover:shadow-sm transition-all">
                    <span className="flex-1 truncate">{res.title || res}</span>
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-500 ml-2" />
                  </div>
                ))}
                {stageContent.resources.length === 0 && (
                  <p className="text-sm text-slate-400 italic col-span-2 text-center py-4">No specific resources for this stage</p>
                )}
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Engagement Notes"
              isOpen={expandedSections.notes}
              onToggle={() => toggleSection('notes')}
              icon={<MessageSquare className="w-5 h-5" />}
            >
              <div className="space-y-3">
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Capture a summary of your interactions..."
                  className="min-h-[200px] text-sm resize-none border-slate-200 focus:ring-blue-500"
                />
                <div className="flex items-center justify-between">
                  {saveStatus && (
                    <div className={`text-xs font-medium flex items-center gap-1 ${saveStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {saveStatus.type === 'success' ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                      {saveStatus.msg}
                    </div>
                  )}
                  <Button
                    size="sm"
                    disabled={isSavingNotes}
                    onClick={handleSaveNotes}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                  >
                    {isSavingNotes ? 'Saving...' : <><Save className="w-3 h-3" /> Save Notes</>}
                  </Button>
                </div>
              </div>
            </CollapsibleSection>

            {/* Lead Generation: Free 1-Pager Website Workflow */}
                        {isLead && isLeadGen && (
                          <>
                            <CollapsibleSection
                              title="Internal Activities"
                              isOpen={expandedSections.internal}
                              onToggle={() => toggleSection('internal')}
                              icon={<Target className="w-5 h-5" />}
                            >
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <ActivityCard
                                  label="Freedom Number"
                                  data={internalActivities?.freedomNumber}
                                  onChange={(val) => handleSaveActivities({ freedomNumber: val })}
                                  isSaving={isSavingActivities}
                                />
                                <ActivityCard
                                  label="30-Day Inception"
                                  data={internalActivities?.thirtyDayInception}
                                  onChange={(val) => handleSaveActivities({ thirtyDayInception: val })}
                                  isSaving={isSavingActivities}
                                />
                                <ActivityCard
                                  label="Rule of 100"
                                  data={internalActivities?.ruleOf100}
                                  onChange={(val) => handleSaveActivities({ ruleOf100: val })}
                                  isSaving={isSavingActivities}
                                />
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-5 h-5 rounded-full border-2 ${internalActivities?.starvingCrowd ? 'bg-green-500 border-green-600' : 'bg-slate-200 border-slate-300'}`} />
                                    <span className="text-sm font-medium text-slate-700">Starving Crowd Identified</span>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleSaveActivities({ starvingCrowd: !internalActivities?.starvingCrowd })}
                                    disabled={isSavingActivities}
                                  >
                                    Toggle
                                  </Button>
                                </div>
                              </div>
                            </CollapsibleSection>
                
                            {/* Free 1-Pager Website Delivery Workflow */}
                            <CollapsibleSection
                              title="1-Pager Website Delivery"
                              isOpen={expandedSections.steps}
                              onToggle={() => toggleSection('steps')}
                              icon={<Zap className="w-5 h-5" />}
                            >
                              <div className="space-y-4">
                                {/* Step 1: Referral Verification */}
                                <div className="border-l-2 border-slate-200 pl-4 ml-2 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {completedTasks.get('step-0')?.completed ? (
                                        <CheckCircle className="w-6 h-6 text-green-500" />
                                      ) : (
                                        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
                                          1
                                        </div>
                                      )}
                                      <h4 className={`font-semibold flex items-center gap-3 ${completedTasks.get('step-0')?.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                                                                              Verify 5 Referrals Submitted
                                                                              {completedTasks.get('step-0')?.completed && (
                                                                                <span className="text-[10px] text-slate-400 font-normal italic ml-1">
                                                                                  {completedTasks.get('step-0')?.timestamp}
                                                                                </span>
                                                                              )}
                                                                            </h4>
                                      <Checkbox
                                        checked={completedTasks.get('step-0')?.completed}
                                        onChange={(e) => handleTaskCompletion({
                                          email: client.email,
                                          stage: client.currentStage,
                                          stepIndex: 0,
                                          completed: e.target.checked,
                                          taskType: 'step'
                                        })}
                                        className="h-4 w-4"
                                      />
                                    </div>
                                    <div className="text-[10px] text-slate-500">
                                      Track referrals in CRM_Leads → Referral Status column
                                    </div>
                                  </div>
                                  <div className="text-[9px] text-slate-400 italic mt-1">
                                    Once 5 referrals accept, trigger free website delivery
                                  </div>
                                </div>
                    
                                {/* Step 2: Free Website Delivery */}
                                <div className="border-l-2 border-slate-200 pl-4 ml-2 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {completedTasks.get('step-1')?.completed ? (
                                        <CheckCircle className="w-6 h-6 text-green-500" />
                                      ) : (
                                        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
                                          2
                                        </div>
                                      )}
                                      <h4 className={`font-semibold flex items-center gap-3 ${completedTasks.get('step-1')?.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                                                                              Deliver Free 1-Pager Website
                                                                              {completedTasks.get('step-1')?.completed && (
                                                                                <span className="text-[10px] text-slate-400 font-normal italic ml-1">
                                                                                  {completedTasks.get('step-1')?.timestamp}
                                                                                </span>
                                                                              )}
                                                                            </h4>
                                      <Checkbox
                                        checked={completedTasks.get('step-1')?.completed}
                                        onChange={(e) => handleTaskCompletion({
                                          email: client.email,
                                          stage: client.currentStage,
                                          stepIndex: 1,
                                          completed: e.target.checked,
                                          taskType: 'step'
                                        })}
                                        className="h-4 w-4"
                                      />
                                    </div>
                                    <div className="text-[10px] text-slate-500">
                                      Build via Website Builder, deploy to Cloudflare Pages, share preview link
                                    </div>
                                  </div>
                                  <div className="text-[9px] text-slate-400 italic mt-1">
                                    Client reviews Cloudflare Pages link (aim for 4/5 satisfaction score)
                                  </div>
                                </div>
                    
                                {/* Step 3: Hosting Handshake (Tripwire) */}
                                <div className="border-l-2 border-slate-200 pl-4 ml-2 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {completedTasks.get('step-2')?.completed ? (
                                        <CheckCircle className="w-6 h-6 text-green-500" />
                                      ) : (
                                        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
                                          3
                                        </div>
                                      )}
                                      <h4 className={`font-semibold flex items-center gap-3 ${completedTasks.get('step-2')?.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                                                                              Hosting Handshake: R197/yr Managed Domain
                                                                              {completedTasks.get('step-2')?.completed && (
                                                                                <span className="text-[10px] text-slate-400 font-normal italic ml-1">
                                                                                  {completedTasks.get('step-2')?.timestamp}
                                                                                </span>
                                                                              )}
                                                                            </h4>
                                      <Checkbox
                                        checked={completedTasks.get('step-2')?.completed}
                                        onChange={(e) => handleTaskCompletion({
                                          email: client.email,
                                          stage: client.currentStage,
                                          stepIndex: 2,
                                          completed: e.target.checked,
                                          taskType: 'step'
                                        })}
                                        className="h-4 w-4"
                                      />
                                    </div>
                                    <div className="text-[10px] text-slate-500">
                                      Offer .co.za (R197/yr) or .com (R397/yr) managed Cloudflare hosting
                                    </div>
                                  </div>
                                  <div className="text-[9px] text-slate-400 italic mt-1">
                                    Upon payment: provision domain, update status to Customer, begin onboarding
                                  </div>
                                </div>
                              </div>
                            </CollapsibleSection>
                          </>
                        )}
            
                        {/* Lead Conversion: Complete Engagement → Conversion Workflow */}
                        {client.currentStage === 'Lead Conversion' && (
                          <>
                            {/* Phase 2: Engagement & Qualification */}
                            <CollapsibleSection
                              title="Phase 2: Engagement & Qualification"
                              isOpen={expandedSections.steps}
                              onToggle={() => toggleSection('steps')}
                              icon={<Target className="w-5 h-5" />}
                            >
                              <div className="space-y-4">
                                {/* BANT Qualification */}
                                <div className="border-l-2 border-slate-200 pl-4 ml-2 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {completedTasks.get('bant')?.completed ? (
                                        <CheckCircle className="w-6 h-6 text-green-500" />
                                      ) : (
                                        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
                                          B
                                        </div>
                                      )}
                                      <h4 className={`font-semibold flex items-center gap-3 ${completedTasks.get('bant')?.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                                        BANT Qualification
                                        {completedTasks.get('bant')?.completed && (
                                          <span className="text-[10px] text-slate-400 font-normal italic ml-1">
                                            {completedTasks.get('bant')?.timestamp}
                                          </span>
                                        )}
                                      </h4>
                                      <Checkbox
                                        checked={completedTasks.get('bant')?.completed}
                                        onChange={(e) => handleTaskCompletion({
                                          email: client.email,
                                          stage: client.currentStage,
                                          stepIndex: 'bant',
                                          completed: e.target.checked,
                                          taskType: 'step'
                                        })}
                                        className="h-4 w-4"
                                      />
                                    </div>
                                    <div className="text-[10px] text-slate-500">
                                      Budget (R197+/mo), Authority, Need (1-10), Timeline (30-day readiness)
                                    </div>
                                  </div>
                                </div>
                    
                                {/* Hosting Handshake (Tripwire) */}
                                <div className="border-l-2 border-slate-200 pl-4 ml-2 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {completedTasks.get('hosting')?.completed ? (
                                        <CheckCircle className="w-6 h-6 text-green-500" />
                                      ) : (
                                        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
                                          H
                                        </div>
                                      )}
                                      <h4 className={`font-semibold flex items-center gap-3 ${completedTasks.get('hosting')?.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                                                                              Hosting Handshake (Tripwire)
                                                                              {completedTasks.get('hosting')?.completed && (
                                                                                <span className="text-[10px] text-slate-400 font-normal italic ml-1">
                                                                                  {completedTasks.get('hosting')?.timestamp}
                                                                                </span>
                                                                              )}
                                                                            </h4>
                                      <Checkbox
                                        checked={completedTasks.get('hosting')?.completed}
                                        onChange={(e) => handleTaskCompletion({
                                          email: client.email,
                                          stage: client.currentStage,
                                          stepIndex: 'hosting',
                                          completed: e.target.checked,
                                          taskType: 'step'
                                        })}
                                        className="h-4 w-4"
                                      />
                                    </div>
                                    <div className="text-[10px] text-slate-500">
                                      Offer managed Cloudflare domain hosting (R197/.co.za or R397/.com)
                                    </div>
                                  </div>
                                </div>
                    
                                {/* Discovery Call Scheduler */}
                                <div className="border-l-2 border-slate-200 pl-4 ml-2 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {completedTasks.get('discovery')?.completed ? (
                                        <CheckCircle className="w-6 h-6 text-green-500" />
                                      ) : (
                                        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
                                          D
                                        </div>
                                      )}
                                      <h4 className={`font-semibold flex items-center gap-3 ${completedTasks.get('discovery')?.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                                                                              15-Min Discovery Call
                                                                              {completedTasks.get('discovery')?.completed && (
                                                                                <span className="text-[10px] text-slate-400 font-normal italic ml-1">
                                                                                  {completedTasks.get('discovery')?.timestamp}
                                                                                </span>
                                                                              )}
                                                                            </h4>
                                      <Checkbox
                                        checked={completedTasks.get('discovery')?.completed}
                                        onChange={(e) => handleTaskCompletion({
                                          email: client.email,
                                          stage: client.currentStage,
                                          stepIndex: 'discovery',
                                          completed: e.target.checked,
                                          taskType: 'step'
                                        })}
                                        className="h-4 w-4"
                                      />
                                    </div>
                                    <div className="text-[10px] text-slate-500">
                                      Specific Purpose Statement: Understand goals, determine fit, set next steps
                                    </div>
                                  </div>
                                </div>
                    
                                {/* Recommendation Pivot */}
                                <div className="border-l-2 border-slate-200 pl-4 ml-2 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {completedTasks.get('recommendation')?.completed ? (
                                        <CheckCircle className="w-6 h-6 text-green-500" />
                                      ) : (
                                        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
                                          R
                                        </div>
                                      )}
                                      <h4 className={`font-semibold flex items-center gap-3 ${completedTasks.get('recommendation')?.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                                                                              Recommendation Pivot
                                                                              {completedTasks.get('recommendation')?.completed && (
                                                                                <span className="text-[10px] text-slate-400 font-normal italic ml-1">
                                                                                  {completedTasks.get('recommendation')?.timestamp}
                                                                                </span>
                                                                              )}
                                                                            </h4>
                                      <Checkbox
                                        checked={completedTasks.get('recommendation')?.completed}
                                        onChange={(e) => handleTaskCompletion({
                                          email: client.email,
                                          stage: client.currentStage,
                                          stepIndex: 'recommendation',
                                          completed: e.target.checked,
                                          taskType: 'step'
                                        })}
                                        className="h-4 w-4"
                                      />
                                    </div>
                                    <div className="text-[10px] text-slate-500">
                                      Based on pain → prescribe best program/path solution
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </CollapsibleSection>
                
                            {/* Phase 3: Conversion & Onboarding */}
                            <CollapsibleSection
                              title="Phase 3: Conversion & Onboarding"
                              isOpen={expandedSections.steps}
                              onToggle={() => toggleSection('steps')}
                              icon={<Zap className="w-5 h-5" />}
                            >
                              <div className="space-y-4">
                                {/* 4 P's Demo */}
                                <div className="border-l-2 border-slate-200 pl-4 ml-2 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {completedTasks.get('demo')?.completed ? (
                                        <CheckCircle className="w-6 h-6 text-green-500" />
                                      ) : (
                                        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
                                          4P
                                        </div>
                                      )}
                                      <h4 className={`font-semibold flex items-center gap-3 ${completedTasks.get('demo')?.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                                                                              4 P's Persuasive Demo
                                                                              {completedTasks.get('demo')?.completed && (
                                                                                <span className="text-[10px] text-slate-400 font-normal italic ml-1">
                                                                                  {completedTasks.get('demo')?.timestamp}
                                                                                </span>
                                                                              )}
                                                                            </h4>
                                      <Checkbox
                                        checked={completedTasks.get('demo')?.completed}
                                        onChange={(e) => handleTaskCompletion({
                                          email: client.email,
                                          stage: client.currentStage,
                                          stepIndex: 'demo',
                                          completed: e.target.checked,
                                          taskType: 'step'
                                        })}
                                        className="h-4 w-4"
                                      />
                                    </div>
                                    <div className="text-[10px] text-slate-500">
                                      Picture, Promise, Proof, Push - start with Killer Feature
                                    </div>
                                  </div>
                                </div>
                    
                                {/* 1-Page Proposal */}
                                <div className="border-l-2 border-slate-200 pl-4 ml-2 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {completedTasks.get('proposal')?.completed ? (
                                        <CheckCircle className="w-6 h-6 text-green-500" />
                                      ) : (
                                        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
                                          P
                                        </div>
                                      )}
                                      <h4 className={`font-semibold flex items-center gap-3 ${completedTasks.get('proposal')?.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                                                                              1-Page Proposal
                                                                              {completedTasks.get('proposal')?.completed && (
                                                                                <span className="text-[10px] text-slate-400 font-normal italic ml-1">
                                                                                  {completedTasks.get('proposal')?.timestamp}
                                                                                </span>
                                                                              )}
                                                                            </h4>
                                      <Checkbox
                                        checked={completedTasks.get('proposal')?.completed}
                                        onChange={(e) => handleTaskCompletion({
                                          email: client.email,
                                          stage: client.currentStage,
                                          stepIndex: 'proposal',
                                          completed: e.target.checked,
                                          taskType: 'step'
                                        })}
                                        className="h-4 w-4"
                                      />
                                    </div>
                                    <div className="text-[10px] text-slate-500">
                                      Goal, Problem, Solution, Investment, Next Step + risk reversal
                                    </div>
                                  </div>
                                </div>
                    
                                {/* 3-Touch Payment Follow-Up */}
                                <div className="border-l-2 border-slate-200 pl-4 ml-2 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {completedTasks.get('followup')?.completed ? (
                                        <CheckCircle className="w-6 h-6 text-green-500" />
                                      ) : (
                                        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
                                          3T
                                        </div>
                                      )}
                                      <h4 className={`font-semibold flex items-center gap-3 ${completedTasks.get('followup')?.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                                                                              3-Touch Payment Follow-Up
                                                                              {completedTasks.get('followup')?.completed && (
                                                                                <span className="text-[10px] text-slate-400 font-normal italic ml-1">
                                                                                  {completedTasks.get('followup')?.timestamp}
                                                                                </span>
                                                                              )}
                                                                            </h4>
                                      <Checkbox
                                        checked={completedTasks.get('followup')?.completed}
                                        onChange={(e) => handleTaskCompletion({
                                          email: client.email,
                                          stage: client.currentStage,
                                          stepIndex: 'followup',
                                          completed: e.target.checked,
                                          taskType: 'step'
                                        })}
                                        className="h-4 w-4"
                                      />
                                    </div>
                                    <div className="text-[10px] text-slate-500">
                                      Touch 1 (48h): Helpful check-in • Touch 2 (96h): Calendar lock • Touch 3 (7d): Bless & release
                                    </div>
                                  </div>
                                </div>
                    
                                {/* Close & Provision (Golden Handshake) */}
                                <div className="border-l-2 border-slate-200 pl-4 ml-2 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {completedTasks.get('provision')?.completed ? (
                                        <CheckCircle className="w-6 h-6 text-green-500" />
                                      ) : (
                                        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
                                          GP
                                        </div>
                                      )}
                                      <h4 className={`font-semibold flex items-center gap-3 ${completedTasks.get('provision')?.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                                                                              Close & Provision (Golden Handshake)
                                                                              {completedTasks.get('provision')?.completed && (
                                                                                <span className="text-[10px] text-slate-400 font-normal italic ml-1">
                                                                                  {completedTasks.get('provision')?.timestamp}
                                                                                </span>
                                                                              )}
                                                                            </h4>
                                      <Checkbox
                                        checked={completedTasks.get('provision')?.completed}
                                        onChange={(e) => handleTaskCompletion({
                                          email: client.email,
                                          stage: client.currentStage,
                                          stepIndex: 'provision',
                                          completed: e.target.checked,
                                          taskType: 'step'
                                        })}
                                        className="h-4 w-4"
                                      />
                                    </div>
                                    <div className="text-[10px] text-slate-500">
                                      On payment: Provisioning Engine → Welcome Pack → 3 Activation Tasks
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </CollapsibleSection>
                          </>
                        )}

            {/* Phase 3 — Fulfillment UI */}
            <ProvisioningStatus client={client} />
            <ActivationTasks client={client} />
            <LessonUnlock client={client} />

            {/* Phase 3 — Retention UI */}
            <HealthCheckPanel client={client} />
            <MonthlyReview client={client} />
            <GoalUpsell client={client} />

            {/* Phase 3 — Growth UI */}
            <UpsellTracker client={client} />
            <SOPDocumenter client={client} />
            <SegmentationTool client={client} />

            {/* Phase 3 — Advocacy UI */}
            <ReviewRequester client={client} />
            <AdvisoryBoard client={client} />
            <CoCreationWizard client={client} />
            <ReferralEnrolment client={client} />
          </div>
        </TabsContent>
        <TabsContent value="communications" className="mt-6 space-y-6">
          <CommunicationHistory clientEmail={client.email} clientPhone={client.phone} clientCustomerId={client.id.replace(/^(customer_|lead_)/, '')} />
        </TabsContent>

        <TabsContent value="intelligence" className="mt-6 space-y-6">
          <IntelligencePanel client={client} />
        </TabsContent>

        <TabsContent value="details" className="mt-6 space-y-6">
          <Card className="bg-white shadow-sm border-slate-200">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                Client Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Company</label>
                    <Input value={client.company || ''} readOnly className="bg-slate-50" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Shared Folder URL</label>
                    <div className="flex gap-2">
                      <Input value={client.sharedFolderUrl || ''} readOnly className="bg-slate-50" />
                      <Button variant="ghost" size="sm" className="px-2" asChild>
                        <a href={client.sharedFolderUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Last Active</label>
                    <Input value={client.lastActive || 'N/A'} readOnly className="bg-slate-50" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Referred By</label>
                    <Input value={client.referredBy || 'N/A'} readOnly className="bg-slate-50" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="finance" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FinancialStatCard label="Lifetime Value" value={`$${client.ltv || 0}`} color="blue" />
            <FinancialStatCard label="Commission Due" value={`$${client.commissionDue || 0}`} color="green" />
            <FinancialStatCard label="Satisfaction Score" value={`${client.satisfactionScore || 0}/5`} color="purple" />
          </div>
          <Card className="bg-white shadow-sm border-slate-200">
            <CardContent className="p-6 text-center text-slate-400 italic">
              Detailed financial ledger is coming soon in the full Financials view.
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// --- Sub-components ---

const MetricCard = ({ label, value, icon, color }: any) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600',
    yellow: 'bg-yellow-50 text-yellow-600',
  };
  const selectedColor = colorClasses[color as keyof typeof colorClasses] || colorClasses.blue;

  return (
    <Card className="bg-white shadow-sm border-slate-200">
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`p-2 rounded-lg ${selectedColor}`}>
          {icon}
        </div>
        <div className="flex-1">
          <p className="text-xs text-slate-500 font-medium uppercase">{label}</p>
          <p className="text-sm font-semibold text-slate-900">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
};

const CollapsibleSection = ({ title, isOpen, onToggle, icon, children }: any) => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden transition-all">
    <button
      onClick={onToggle}
      className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
    >
      <div className="flex items-center gap-3 text-slate-800 font-bold">
        <div className="p-1.5 bg-slate-100 rounded-lg text-slate-500">{icon}</div>
        {title}
      </div>
      {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
    </button>
    {isOpen && (
      <div className="p-4 pt-0 border-t border-slate-100">
        <div className="pt-4 pb-2">{children}</div>
      </div>
    )}
  </div>
);

const ActivityCard = ({ label, data, onChange, isSaving }: any) => {
  const [local, setLocal] = useState(data || { goal: 0, achieved: 0 });

  useEffect(() => {
    if (data) {setLocal(data);}
  }, [data]);

  const __update = (field: string, val: number) => {
    const next = { ...local, [field]: val };
    setLocal(next);
    onChange(next);
  };

  const variance = (local?.goal || 0) - (local?.achieved || 0);

  return (
    <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-slate-700">{label}</span>
        <div className={`text-xs font-bold px-2 py-0.5 rounded-full ${variance <= 0 ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
          Var: {variance}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-slate-400 uppercase">Goal</label>
          <Input
            type="number"
            value={local?.goal || ''}
            onChange={(e) => __update('goal', parseFloat(e.target.value) || 0)}
            className="h-8 text-sm"
            disabled={isSaving}
          />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-slate-400 uppercase">Achieved</label>
          <Input
            type="number"
            value={local?.achieved || ''}
            onChange={(e) => __update('achieved', parseFloat(e.target.value) || 0)}
            className="h-8 text-sm"
            disabled={isSaving}
          />
        </div>
      </div>
    </div>
  );
};

const FinancialStatCard = ({ label, value, color }: any) => {
  const colorClasses = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    red: 'text-red-600',
    yellow: 'text-yellow-600',
  };
  const selectedColor = colorClasses[color as keyof typeof colorClasses] || colorClasses.blue;

  return (
    <Card className="bg-white shadow-sm border-slate-200">
      <CardContent className="p-4 flex flex-col items-center justify-center text-center">
        <p className={`text-xs font-bold uppercase tracking-wider text-slate-500 mb-1`}>{label}</p>
        <p className={`text-2xl font-black ${selectedColor}`}>{value}</p>
      </CardContent>
    </Card>
  );
};

export default ClientDetailView;
